import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import { PARNAS_TYPE_LABELS, fetchHebDateLabel } from '../../parnasConstants';

/**
 * WeeksAheadWeekly — נציב שבועי מפואר + N שבועות קדימה
 * מלבן גדול ומלכותי לנציב השבוע הנוכחי (פרשה + שם),
 * ומתחתיו רשת של שבועות קדימה — כל תא מציג שם פרשה, שבת עברי ושם הנציב.
 */

const GOLD = '#b8860b';

// ─── Component ────────────────────────────────────────────────────────────────

const WeeksAheadWeekly = ({
  activeSponsors            = [],
  parnasType                = 'maor',
  parnasTypeCustomLabel     = '',
  parnasTypeIcon            = 'star',
  currentParshaName         = '',
  currentParshaShabbatDate  = '',
  todayHebrewLabel          = '',
  title                     = '',
  showTitle                 = false,
  sponsors                  = [],
  effectiveDateISO          = '',
  placeholderName           = 'השם שלך כאן',
  weeksAheadCount           = 8,
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  const todayWeekly = activeSponsors.find(s => s.slotType === 'weekly') || null;

  const weeklySponsorByDate = useMemo(() => {
    const map = {};
    for (const sponsor of sponsors)
      for (const slot of (sponsor.slots || []))
        if (slot.type === 'weekly' && slot.parshaShabbatDate)
          map[slot.parshaShabbatDate] = { sponsorName: sponsor.name, note: slot.note || '' };
    return map;
  }, [sponsors]);

  // ── Hebrew date of current parsha Shabbat + upcoming parshiyot (parallel) ─
  const [currentHebDate,    setCurrentHebDate]    = useState('');
  const [upcomingParshiyot, setUpcomingParshiyot] = useState([]);
  const [loadingParshot,    setLoadingParshot]    = useState(false);

  useEffect(() => {
    if (!currentParshaShabbatDate && !effectiveDateISO) return;
    let cancelled = false;
    setLoadingParshot(true);

    const baseDate = currentParshaShabbatDate || effectiveDateISO;
    const yr       = new Date(baseDate + 'T12:00:00').getFullYear();

    Promise.all([
      currentParshaShabbatDate
        ? fetchHebDateLabel(currentParshaShabbatDate)
        : Promise.resolve(''),
      Promise.all([
        fetch(`https://www.hebcal.com/hebcal?v=1&cfg=json&year=${yr}&yt=G&s=on&maj=on&c=off`).then(r => r.json()),
        fetch(`https://www.hebcal.com/hebcal?v=1&cfg=json&year=${yr + 1}&yt=G&s=on&maj=on&c=off`).then(r => r.json()),
      ]),
    ]).then(([currentLabel, [d1, d2]]) => {
      if (cancelled) return;
      if (currentLabel) setCurrentHebDate(currentLabel);

      const allParshot = [
        ...(d1.items || []).filter(i => i.category === 'parashat'),
        ...(d2.items || []).filter(i => i.category === 'parashat'),
      ].sort((a, b) => (a.date < b.date ? -1 : 1));

      const currentIdx = allParshot.findIndex(p => p.date === currentParshaShabbatDate);
      const upcoming   = currentIdx >= 0
        ? allParshot.slice(currentIdx + 1, currentIdx + 1 + weeksAheadCount)
        : allParshot.filter(p => p.date > (effectiveDateISO || '')).slice(0, weeksAheadCount);

      setUpcomingParshiyot(upcoming);
      setLoadingParshot(false);
    }).catch(() => { if (!cancelled) setLoadingParshot(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParshaShabbatDate, effectiveDateISO, weeksAheadCount]);

  // ── Hebrew dates for upcoming parshiyot ───────────────────────────────────
  const [hebDates, setHebDates] = useState({});
  useEffect(() => {
    if (!upcomingParshiyot.length) return;
    let cancelled = false;
    Promise.all(
      upcomingParshiyot.map(({ date }) =>
        fetchHebDateLabel(date).then(label => ({ date, label }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ date, label }) => { map[date] = label; });
      setHebDates(map);
    });
    return () => { cancelled = true; };
  }, [upcomingParshiyot]);

  // Number of columns: up to 4 per row
  const cols = Math.min(4, weeksAheadCount);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box dir="rtl" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{
        px: 2, pt: 1.5, pb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75,
        flexShrink: 0,
      }}>
        <TypeIcon sx={{ fontSize: 18, color: heritageColors.gold }} />
        <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
          {showTitle && title ? title : `פרנסי ${typeLabel}`}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />

      {/* Hero card — current week's weekly sponsor */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flex: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          flex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${GOLD}`, borderRadius: '8px', px: 3, py: 2,
          textAlign: 'center', gap: 1,
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(160deg, #fffdf7 0%, #fef8e4 55%, #fdf1cc 100%)',
          boxShadow: '0 4px 20px rgba(184,134,11,0.20), 0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {/* Top gold accent stripe */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(to left, transparent, ${GOLD} 25%, #d4a017 50%, ${GOLD} 75%, transparent)`,
          }} />

          <Typography variant="caption" sx={{
            color: GOLD, fontWeight: 700, fontSize: '0.68em', letterSpacing: '0.08em',
          }}>
            {'נציב השבוע'}
            {currentParshaName ? `  ·  פרשת ${currentParshaName}` : ''}
            {currentHebDate    ? `  ·  שבת ${currentHebDate}`      : ''}
          </Typography>

          <Box sx={{ width: '40%', height: '1px', bgcolor: GOLD, opacity: 0.3, my: 0.25 }} />

          <Typography variant="h4" sx={{
            color:      todayWeekly ? heritageColors.navy : heritageColors.slate,
            fontWeight: 800, lineHeight: 1.2,
            opacity:    todayWeekly ? 1 : 0.3, wordBreak: 'break-word',
          }}>
            {todayWeekly ? todayWeekly.sponsorName : placeholderName}
          </Typography>

          {!todayWeekly && (
            <Typography variant="body2" sx={{
              color: heritageColors.slate, fontSize: '0.72em', mt: 0.25, opacity: 0.55, lineHeight: 1.3,
            }}>
              ניתן לתרום לשבוע זה — פנה לגבאי בית הכנסת
            </Typography>
          )}

          {todayWeekly?.note && (
            <Typography variant="body2" sx={{
              color: heritageColors.slate, fontStyle: 'italic', fontSize: '0.75em', mt: 0.25, lineHeight: 1.3,
            }}>
              {todayWeekly.note}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Section label */}
      <Box sx={{
        px: 2, pt: 0.25, pb: 0.5, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Typography variant="caption" sx={{
          color: heritageColors.slate, fontSize: '0.65em', fontWeight: 600,
          letterSpacing: '0.05em', whiteSpace: 'nowrap',
        }}>
          {weeksAheadCount} שבועות קדימה
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: heritageColors.border }} />
      </Box>

      {/* Upcoming weeks grid */}
      <Box sx={{
        flex: 3,
        minHeight: 0,
        px: 1.25, pb: 1.25,
        overflow: 'hidden',
        display: loadingParshot ? 'flex' : 'grid',
        ...(loadingParshot
          ? { alignItems: 'center', justifyContent: 'center' }
          : {
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridAutoRows:        '1fr',
              gap:                 '4px',
            }
        ),
      }}>
        {loadingParshot ? (
          <CircularProgress size={20} sx={{ color: GOLD }} />
        ) : upcomingParshiyot.map(({ date, hebrew, title: engTitle }) => {
          const sponsor    = weeklySponsorByDate[date] || null;
          const hebLabel   = hebDates[date] || '…';
          const parshaName = hebrew || engTitle || '';

          return (
            <Box key={date} sx={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'flex-start',
              border: `1.5px solid ${sponsor ? GOLD : heritageColors.border}`,
              borderRadius: '6px',
              px: 0.5, py: 1,
              textAlign: 'center', gap: '3px',
              background: sponsor
                ? 'linear-gradient(to bottom, #fffdf7 0%, #fdf8e8 100%)'
                : 'linear-gradient(to bottom, #fafaf9 0%, #f5f5f3 100%)',
              boxShadow: sponsor
                ? '0 2px 10px rgba(184,134,11,0.14)'
                : '0 1px 3px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              {/* Parsha name */}
              <Typography sx={{
                color: heritageColors.navy, fontSize: '0.62em', fontWeight: 700,
                lineHeight: 1.2, wordBreak: 'break-word',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
              }}>
                {parshaName}
              </Typography>

              {/* Hebrew Shabbat date */}
              <Typography sx={{
                color: heritageColors.slate, fontSize: '0.54em',
                opacity: 0.7, lineHeight: 1.1, whiteSpace: 'nowrap',
              }}>
                {hebLabel}
              </Typography>

              <Box sx={{
                width: '55%', height: '1px',
                bgcolor: sponsor ? GOLD : heritageColors.border,
                opacity: 0.4, my: '2px',
              }} />

              {sponsor ? (
                <Typography sx={{
                  color: heritageColors.navy, fontWeight: 700,
                  fontSize: '0.6em', lineHeight: 1.2, wordBreak: 'break-word',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                }}>
                  {sponsor.sponsorName}
                </Typography>
              ) : (
                <>
                  <Typography sx={{
                    color: heritageColors.gold, fontWeight: 700, fontSize: '0.58em', lineHeight: 1.2,
                  }}>
                    פנוי
                  </Typography>
                  <Typography sx={{
                    color: heritageColors.slate, fontSize: '0.5em',
                    opacity: 0.65, lineHeight: 1.2, mt: '1px',
                  }}>
                    לזכות לתרום
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {(currentParshaName || todayHebrewLabel) && (
        <>
          <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />
          <Box sx={{
            px: 2, py: 0.6, flexShrink: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {currentParshaName ? (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                פרשת {currentParshaName}
              </Typography>
            ) : <Box />}
            {todayHebrewLabel && (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                {todayHebrewLabel}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WeeksAheadWeekly;
